import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Bus, ChevronRight, Home, LocateFixed, Mail, RefreshCw } from 'lucide-react';
import { useRecorridosVisibles } from '@/hooks/useRecorridosVisibles';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { trackSeleccionRecorrido } from '@/lib/analytics';
import { MENSAJE_BUS_REEMPLAZO_SIN_TIEMPO_REAL } from '@/lib/paraderoRecorrido';
import { CERCA_PATH, paraderoPath } from '@/lib/paraderoRoutes';
import { setStoredRecorridoId } from '@/lib/recorridoStorage';
import { cn } from '@/lib/utils';
import { getLandingCardColors } from '@/data/recorridos/config';

export default function LandingView() {
  const location = useLocation();
  const navigate = useNavigate();
  const routedError = location.state?.error;
  const { recorridos: recorridosLista } = useRecorridosVisibles();

  useDocumentTitle(null);

  const goToParadero = (recorridoId) => {
    setStoredRecorridoId(recorridoId);
    trackSeleccionRecorrido(recorridoId);
    navigate(paraderoPath({ recorridoId }));
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-background px-4 pb-32 pt-10 sm:pt-14">
      <div
        className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-primary/[0.06] blur-[100px]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -left-40 top-1/2 h-80 w-80 rounded-full bg-secondary/[0.08] blur-[90px]"
        aria-hidden
      />

      <div className="relative z-10 mx-auto flex max-w-lg flex-col items-center">
        <header className="mb-8 w-full text-center">
          <div className="mb-5 flex justify-center">
            <Link
              to="/"
              className="inline-flex rounded-xl outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Ir al inicio"
            >
              <img
                src={`${process.env.PUBLIC_URL || ''}/img/Logo-MuniBus.png`}
                alt=""
                className="h-16 w-auto drop-shadow-md"
              />
            </Link>
          </div>
          <h1 className="font-headline text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
            Bienvenido a <span className="text-primary">MuniBus</span>
          </h1>
          <p className="mx-auto mt-2 max-w-[280px] text-base font-medium text-muted-foreground">
            Busca tu paradero o selecciona un recorrido
          </p>
        </header>

        {routedError && (
          <div
            className="mb-6 w-full rounded-2xl border border-destructive/35 bg-destructive/10 px-4 py-3 text-sm text-destructive"
            role="alert"
            data-testid="landing-routed-error"
          >
            {routedError}
          </div>
        )}

        <Link
          to={CERCA_PATH}
          className="group mb-6 flex w-full items-center justify-between rounded-2xl border-2 border-primary/30 bg-card p-4 text-left shadow-card transition-all hover:border-primary/60 hover:shadow-md active:scale-[0.99]"
          data-testid="landing-cerca"
        >
          <span className="flex min-w-0 items-center gap-4">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary/10">
              <LocateFixed className="h-7 w-7 text-primary" aria-hidden />
            </span>
            <span className="min-w-0">
              <span className="block font-headline text-lg font-bold leading-snug text-foreground">
                Paraderos cerca de mí
              </span>
              <span className="mt-0.5 block text-sm font-medium leading-snug text-muted-foreground">
                Mira en cuántos minutos pasa el bus por tu paradero
              </span>
            </span>
          </span>
          <ChevronRight
            className="h-6 w-6 shrink-0 text-primary/70 transition-transform group-hover:translate-x-0.5"
            aria-hidden
          />
        </Link>

        <div className="w-full space-y-3" data-testid="landing-view">
          {recorridosLista.map((r) => {
            const inactivo = r.activoApi === false;
            const lc = getLandingCardColors(r);

            const innerInactivo = (
              <>
                <div className="flex min-w-0 items-center gap-4">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-muted/50">
                    <Bus className="h-7 w-7 text-muted-foreground" aria-hidden />
                  </div>
                  <div className="min-w-0">
                    <h2 className="font-headline text-lg font-bold leading-snug text-muted-foreground">
                      {r.nombre}
                    </h2>
                    <p className="mt-1.5 text-xs font-medium leading-snug text-muted-foreground">
                      {MENSAJE_BUS_REEMPLAZO_SIN_TIEMPO_REAL}
                    </p>
                  </div>
                </div>
                <ChevronRight className="h-6 w-6 shrink-0 opacity-40 text-muted-foreground" aria-hidden />
              </>
            );

            const innerActivo = (
              <>
                <div className="flex min-w-0 items-center gap-4">
                  <div
                    className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white/20 ring-1 ring-white/25"
                    aria-hidden
                  >
                    <Bus className="h-7 w-7 text-white drop-shadow-sm" aria-hidden />
                  </div>
                  <div className="min-w-0">
                    <h2 className="font-headline text-lg font-bold leading-snug text-white drop-shadow-sm">
                      {r.nombre}
                    </h2>
                  </div>
                </div>
                <ChevronRight
                  className="h-6 w-6 shrink-0 text-white/75 transition-transform group-hover:translate-x-0.5 group-hover:text-white"
                  aria-hidden
                />
              </>
            );

            if (inactivo) {
              return (
                <div
                  key={r.id}
                  data-testid={`landing-recorrido-${r.id}`}
                  className={cn(
                    'flex w-full cursor-not-allowed items-center justify-between rounded-2xl border border-dashed border-border/70 bg-muted/25 p-4 text-left opacity-90',
                    'pointer-events-none select-none'
                  )}
                  role="group"
                  aria-disabled
                  aria-label={`${r.codigo}: servicio con bus de reemplazo, no disponible para abrir`}
                >
                  {innerInactivo}
                </div>
              );
            }

            return (
              <button
                key={r.id}
                type="button"
                onClick={() => goToParadero(r.id)}
                className={cn(
                  'group flex w-full items-center justify-between rounded-2xl border border-white/20 p-4 text-left shadow-md transition-all duration-200',
                  'hover:brightness-[1.06] hover:shadow-lg active:scale-[0.99]',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background'
                )}
                style={{ backgroundColor: lc.background, color: lc.foreground }}
                data-testid={`landing-recorrido-${r.id}`}
              >
                {innerActivo}
              </button>
            );
          })}
        </div>

        {process.env.NODE_ENV === 'development' && (
          <p className="mt-6 w-full text-center text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">Desarrollo:</span>{' '}
            <button
              type="button"
              onClick={() => goToParadero('t1a')}
              className="font-semibold text-primary underline decoration-primary/40 underline-offset-4"
              data-testid="dev-sample-paradero-link"
            >
              Abrir vista recorrido de prueba (T1)
            </button>
          </p>
        )}

        <a
          href="https://www.paine.cl/"
          target="_blank"
          rel="noreferrer"
          className="mt-10 inline-flex items-center justify-center rounded-xl p-2 transition-opacity hover:opacity-85"
          aria-label="Visitar sitio Municipalidad de Paine"
        >
          <img
            src={`${process.env.PUBLIC_URL || ''}/img/LogoPaine.svg`}
            alt="Municipalidad de Paine"
            className="h-10 w-auto"
          />
        </a>
      </div>

      <nav
        className="glass-header fixed bottom-0 left-0 right-0 z-40 rounded-t-3xl border-t border-white/50 shadow-nav-up pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2"
        aria-label="Accesos rápidos"
      >
        <div className="mx-auto flex h-[4.5rem] max-w-md items-center justify-around px-8">
          <Link
            to="/"
            className="flex flex-col items-center gap-1 text-primary transition-colors"
            aria-current="page"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md">
              <Home className="h-5 w-5" aria-hidden />
            </span>
            <span className="text-[10px] font-bold uppercase tracking-widest">Inicio</span>
          </Link>
          <button
            type="button"
            onClick={handleRefresh}
            className="flex flex-col items-center gap-1 text-muted-foreground transition-colors hover:text-primary"
            aria-label="Actualizar inicio"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-full transition-transform active:scale-90">
              <RefreshCw className="h-6 w-6" />
            </span>
            <span className="text-[10px] font-bold uppercase tracking-widest">Sincronizar</span>
          </button>
          <a
            href="mailto:contacto@paine.cl?subject=Consulta%20MuniBus"
            className="flex flex-col items-center gap-1 text-muted-foreground transition-colors hover:text-primary"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-full transition-transform active:scale-90">
              <Mail className="h-6 w-6" aria-hidden />
            </span>
            <span className="text-[10px] font-bold uppercase tracking-widest">Reportar</span>
          </a>
        </div>
      </nav>
    </div>
  );
}
